import { CommonModule } from '@angular/common'
import { NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ArpioSharedModule } from '../shared/arpio-shared.module'
import { LucideIconsModule } from '../shared/lucide-icons.module'
import { AccountRoutingModule } from './account-routing.module'
import { AccountNavigationComponent } from './components/account-navigation/account-navigation.component'
import { AccountRootComponent } from './components/account-root/account-root.component'
import { AccountShellComponent } from './components/account-shell/account-shell.component'
import { AccountWelcomeComponent } from './components/account-welcome/account-welcome.component'
import {
  AppResourcesViewComponent,
  ResourceStatusPillComponent,
} from './components/app-resources-view/app-resources-view.component'
import { ApplicationCreatorAccessMethodPageComponent } from './components/application-creator-access-method-page/application-creator-access-method-page.component'
import { ApplicationCreatorConnectPageComponent } from './components/application-creator-connect-page/application-creator-connect-page.component'
import { ApplicationCreatorTemplateChoicePageComponent } from './components/application-creator-template-choice-page/application-creator-template-choice-page.component'
import { ApplicationCreatorDetailsPageComponent } from './components/application-creator-details-page/application-creator-details-page.component'
import { ApplicationCreatorResourcesPageComponent } from './components/application-creator-resources-page/application-creator-resources-page.component'
import { ApplicationCreatorShellComponent } from './components/application-creator-shell/application-creator-shell.component'
import { ApplicationEditorComponent } from './components/application-editor/application-editor.component'
import { ApplicationComponent } from './components/application/application.component'
import { ConcludeRecoveryDialogComponent } from './components/conclude-recovery-dialog/conclude-recovery-dialog.component'
import { ConcludeTestDialogComponent } from './components/conclude-test-dialog/conclude-test-dialog.component'
import { ConfirmActionDialogComponent } from './components/confirm-action-dialog/confirm-action-dialog.component'
import { IamPermissionIssueComponent } from './components/issue-view/iam-permission-issue/iam-permission-issue.component'
import {
  AmiStorageNotAccessibleIssueComponent,
  CertificateNotFoundIssueComponent,
  ClientErrorIssueComponent,
  DefaultIssueComponent,
  IssueViewComponent,
  KeyPairNotFoundIssueComponent,
  MarketplaceAmiCantBeCopiedIssueComponent,
  MultipleMatchingImagesIssueComponent,
  MarketplaceInstanceCantBeCopiedIssueComponent,
  QuotaIssueComponent,
  RefreshClientErrorIssueComponent,
} from './components/issue-view/issue-view.component'
import { MarketplaceActionRequiredIssueComponent } from './components/issue-view/marketplace-action-required-issue/marketplace-action-required-issue.component'
import { SyncPairPermissionIssueComponent } from './components/issue-view/sync-pair-permission-issue/sync-pair-permission-issue.component'
import { IssuesViewComponent } from './components/issues-view/issues-view.component'
import { LaunchRecoveryDialogComponent } from './components/launch-recovery-dialog/launch-recovery-dialog.component'
import { LaunchTestDialogComponent } from './components/launch-test-dialog/launch-test-dialog.component'
import { RpoChartComponent } from './components/rpo-chart/rpo-chart.component'
import { AccountRetentionPolicyComponent } from './components/account-retention-policy/account-retention-policy.component'
import { UnconfiguredBucketIssueComponent } from './components/issue-view/unconfigured-bucket-issue/unconfigured-bucket-issue.component'
import { UnconfiguredBlobContainerIssueComponent } from './components/issue-view/unconfigured-azure-blob-container-issue/unconfigured-blob-container-issue.component'
import { DynamoDbBackupModeIssueComponent } from './components/issue-view/dynamodb-backup-mode-issue/dynamodb-backup-mode-issue.component'
import { UnconfiguredBucketReplicationIssueComponent } from './components/issue-view/unconfigured-bucket-replication-issue/unconfigured-bucket-replication-issue.component'
import { BucketBackfillFailedIssueComponent } from './components/issue-view/bucket-backfill-failed-issue/bucket-backfill-failed-issue.component'
import { FailbackDialogComponent } from './components/failback-dialog/failback-dialog.component'
import { ProcessStateComponent } from './components/process-state/process-state.component'
import { DrsAgentNotFoundIssueComponent } from './components/issue-view/drs-agent-not-found-issue/drs-agent-not-found-issue.component'
import { UninstallDrsAgentIssueComponent } from './components/issue-view/uninstall-drs-agent-issue/uninstall-drs-agent-issue.component'
import { SupportComponent } from './components/support/support.component'
import { FailbackResourceIssueComponent } from './components/issue-view/failback-resource-issue/failback-resource-issue.component'
import { SubscriptionOverageIssueComponent } from './components/issue-view/subscription-overage-issue/subscription-overage-issue.component'
import { EksClusterPermissionIssueComponent } from './components/issue-view/eks-cluster-permission-issue/eks-cluster-permission-issue.component'
import { PersistentIssueComponent } from './components/issue-view/persistent-issue/persistent-issue.component'
import { NgLetModule } from 'ng-let'
import { SplitButtonModule } from 'primeng/splitbutton'
import { AccountTagsFormControlComponent } from './components/account-tags-form-control/account-tags-form-control.component'
import { AccountTagsComponent } from './components/account-tags/account-tags.component'
import { UnquarantineDialogComponent } from './components/unquarantine-dialog/unquarantine-dialog.component'
import { EksRequiredEndpointInaccessibleIssueComponent } from './components/issue-view/eks-required-endpoint-inaccessible-issue/eks-required-endpoint-inaccessible-issue.component'
import { AccountIntegrationsComponent } from './components/account-integrations/account-integrations.component'
import { AccountIntegrationComponent } from './components/account-integrations/account-integration/account-integration.component'
import { EditIntegrationDialogComponent } from './components/account-integrations/edit-integration-dialog/edit-integration-dialog.component'
import { SpotByNetappSettingsComponent } from './components/account-integrations/edit-integration-dialog/spot-by-netapp-settings/spot-by-netapp-settings.component'
import { ApplicationIntegrationsFormControlComponent } from './components/application-integrations-form-control/application-integrations-form-control.component'
import { ResourcePolicyPermissionIssueComponent } from './components/issue-view/resource-policy-permission-issue/resource-policy-permission-issue.component'
import { ServiceControlPolicyPermissionIssueComponent } from './components/issue-view/service-control-policy-permission-issue/service-control-policy-permission-issue.component'
import { MalwareScanIssueComponent } from './components/issue-view/malware-scan-issue/malware-scan-issue.component'
import { SortPipe } from '../shared/pipes/sort/sort.pipe'
import { DetailedIssueComponent } from './components/issue-view/detailed-issue/detailed-issue.component'
import { TruncateErrorMessagePipe } from '../shared/pipes/truncate-error-message/truncate-error-message.pipe'
import { HighlightSearchPipe } from '../shared/pipes/highlight-search/highlight-search.pipe'
import { ApplicationTestConfigDialogComponent } from './components/application-test-config-dialog/application-test-config-dialog.component'
import { AwsEventLogsComponent } from './components/aws-event-logs/aws-event-logs.component'
import { UpdateAccessDialogComponent } from './components/update-access-dialog/update-access-dialog.component'
import { BucketNameConflictIssueComponent } from './components/issue-view/bucket-name-conflict-issue/bucket-name-conflict-issue.component'
import { AccountResourceConfigsComponent } from './components/account-resource-configs/account-resource-configs.component'
import { NewTargetPlanRequiredIssueComponent } from './components/issue-view/new-target-plan-required-issue/new-target-plan-required-issue.component'
import { UserManagementComponent } from './components/user-management/user-management.component'
import { RoleManagementComponent } from './components/role-management/role-management.component'
import { GroupManagementComponent } from './components/group-management/group-management.component'
import { InviteUsersModalComponent } from './components/invite-users-modal/invite-users-modal.component'
import { EditUserModalComponent } from './components/edit-user-modal/edit-user-modal.component'
import { NewRoleModalComponent } from './components/new-role-modal/new-role-modal.component'
import { EditRoleModalComponent } from './components/edit-role-modal/edit-role-modal.component'
import { NewGroupModalComponent } from './components/new-group-modal/new-group-modal.component'
import { EditGroupModalComponent } from './components/edit-group-modal/edit-group-modal.component'
import { IdpModalComponent } from './components/shared/idp-modal/idp-modal.component'
import { ApiKeyManagementComponent } from './components/api-key-management/api-key-management.component'
import { NewApiKeyModalComponent } from './components/new-api-key-modal/new-api-key-modal.component'
import { EditApiKeyModalComponent } from './components/edit-api-key-modal/edit-api-key-modal.component'
import { ProfileComponent } from './components/profile/profile.component'
import { AccountSettingsTabsComponent } from './components/account-settings-tabs/account-settings-tabs.component'
import { AccountGeneralComponent } from './components/account-general/account-general.component'
import { EnforcementModeNoticeComponent } from './components/enforcement-mode-notice/enforcement-mode-notice.component'
import { AccountNotificationsComponent } from './components/account-notifications/account-notifications.component'
import { AccountIdentityProvidersComponent } from './components/account-identity-providers/account-identity-providers.component'
import { AccessManagementTabsComponent } from './components/access-management-tabs/access-management-tabs.component'
import { ApplicationUpgradeNoticeComponent } from './components/application-upgrade-notice/application-upgrade-notice.component'
import { ApplicationSettingsTabsComponent } from './components/application-settings-tabs/application-settings-tabs.component'
import { UsageBillingComponent } from './components/usage-billing/usage-billing.component'
import { IssueBodyComponent } from './components/issue-view/issue-body/issue-body.component'
import { IssueContentGroupComponent } from './components/issue-view/issue-content-group/issue-content-group.component'

// Everything AccountModule declares is also exported. The internal /test_page showcase
// (in ArpioModule) mounts production components directly, and a curated export list meant
// every new showcase entry had to edit this production file.
const ACCOUNT_DECLARATIONS = [
  AccountRootComponent,
  ApplicationComponent,
  AccountShellComponent,
  AppResourcesViewComponent,
  ApplicationEditorComponent,
  ApplicationCreatorShellComponent,
  ApplicationCreatorDetailsPageComponent,
  ApplicationCreatorAccessMethodPageComponent,
  ApplicationCreatorConnectPageComponent,
  ApplicationCreatorTemplateChoicePageComponent,
  ApplicationCreatorResourcesPageComponent,
  AccountWelcomeComponent,
  AccountNavigationComponent,
  IssuesViewComponent,
  CertificateNotFoundIssueComponent,
  ClientErrorIssueComponent,
  RefreshClientErrorIssueComponent,
  SyncPairPermissionIssueComponent,
  QuotaIssueComponent,
  MarketplaceAmiCantBeCopiedIssueComponent,
  MultipleMatchingImagesIssueComponent,
  MarketplaceInstanceCantBeCopiedIssueComponent,
  AmiStorageNotAccessibleIssueComponent,
  DefaultIssueComponent,
  IssueViewComponent,
  IssueBodyComponent,
  IssueContentGroupComponent,
  LaunchRecoveryDialogComponent,
  ConcludeRecoveryDialogComponent,
  ConcludeTestDialogComponent,
  LaunchTestDialogComponent,
  ApplicationTestConfigDialogComponent,
  AwsEventLogsComponent,
  ResourceStatusPillComponent,
  RpoChartComponent,
  ConfirmActionDialogComponent,
  IamPermissionIssueComponent,
  MalwareScanIssueComponent,
  MarketplaceActionRequiredIssueComponent,
  KeyPairNotFoundIssueComponent,
  AccountRetentionPolicyComponent,
  AccountTagsComponent,
  UnconfiguredBucketIssueComponent,
  DynamoDbBackupModeIssueComponent,
  UnconfiguredBucketReplicationIssueComponent,
  BucketBackfillFailedIssueComponent,
  BucketNameConflictIssueComponent,
  FailbackDialogComponent,
  ProcessStateComponent,
  DrsAgentNotFoundIssueComponent,
  UninstallDrsAgentIssueComponent,
  SupportComponent,
  FailbackResourceIssueComponent,
  SubscriptionOverageIssueComponent,
  EksClusterPermissionIssueComponent,
  EksRequiredEndpointInaccessibleIssueComponent,
  PersistentIssueComponent,
  AccountTagsFormControlComponent,
  UnquarantineDialogComponent,
  AccountIntegrationsComponent,
  AccountIntegrationComponent,
  EditIntegrationDialogComponent,
  SpotByNetappSettingsComponent,
  ApplicationIntegrationsFormControlComponent,
  ResourcePolicyPermissionIssueComponent,
  ServiceControlPolicyPermissionIssueComponent,
  DetailedIssueComponent,
  UpdateAccessDialogComponent,
  SortPipe,
  TruncateErrorMessagePipe,
  HighlightSearchPipe,
  AccountResourceConfigsComponent,
  NewTargetPlanRequiredIssueComponent,
  UnconfiguredBlobContainerIssueComponent,
  UserManagementComponent,
  RoleManagementComponent,
  GroupManagementComponent,
  InviteUsersModalComponent,
  EditUserModalComponent,
  NewRoleModalComponent,
  EditRoleModalComponent,
  NewGroupModalComponent,
  EditGroupModalComponent,
  IdpModalComponent,
  ApiKeyManagementComponent,
  NewApiKeyModalComponent,
  EditApiKeyModalComponent,
  ProfileComponent,
  AccountSettingsTabsComponent,
  AccountGeneralComponent,
  EnforcementModeNoticeComponent,
  AccountNotificationsComponent,
  AccountIdentityProvidersComponent,
  AccessManagementTabsComponent,
  ApplicationUpgradeNoticeComponent,
  ApplicationSettingsTabsComponent,
  UsageBillingComponent,
]

@NgModule({
  declarations: ACCOUNT_DECLARATIONS,
  exports: ACCOUNT_DECLARATIONS,
  imports: [
    CommonModule,
    AccountRoutingModule,
    ReactiveFormsModule,
    ArpioSharedModule,
    FormsModule,
    NgLetModule,
    SplitButtonModule,
    LucideIconsModule,
  ],
})
export class AccountModule {}
